/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState } from 'react'
import { useServiceContext } from '../../../root'
import ActionsMenu from '../../../components/shared/ActionsMenu'
import ModalRemove from './Actions/ModalRemove'
import { useNavigate } from 'react-router'

interface TableMenuProps {
  item: any
  onEdit: () => void
  onDeleteSuccess?: () => void
  onExportStart?: () => void
  onExportEnd?: () => void
}

const TableMenu: React.FC<TableMenuProps> = ({ item, onEdit, onDeleteSuccess, onExportStart, onExportEnd }) => {
  const context = useServiceContext()
  const navigate = useNavigate()
  const { translate } = context.service.filter
  const { toast } = context.service
  const [showRemoveModal, setShowRemoveModal] = useState(false)

  const handleExport = async () => {
    try {
      // Start loading
      if (onExportStart) {
        onExportStart()
      }

      // Export single folder type with entityId parameter
      await context.api.settings.exportSettingsData('FOLDER_TYPES', undefined, item.id, translate)
    } catch (error) {
      toast.error(translate('SETTINGS_IMPORT_FAIL'))
    } finally {
      // End loading
      if (onExportEnd) {
        onExportEnd()
      }
    }
  }

  const handleActivity = () => {
    // Navigate to settings activity page with folder type parameters
    const params = new URLSearchParams({
      id: item.id.toString(),
      type: 'FOLDER_TYPE_TPL',
      itemName: item.name
    })
    navigate(`/settings/activity?${params.toString()}`)
  }

  const settings = {
    menuId: `action-menu-folder-type-${item.id}`,
    items: [] as Array<{ value: string; action: () => void }>
  }

  // Add Edit and Remove options for non-system types
  if (!item.system) {
    settings.items.push(
      {
        value: 'ACTION_EDIT',
        action: onEdit
      },
      {
        value: 'ACTION_REMOVE',
        action: () => {
          setShowRemoveModal(true)
        }
      }
    )
  }

  // Add Export and Activity options for all types
  settings.items.push(
    {
      value: 'ACTION_EXPORT',
      action: () => {
        handleExport()
      }
    },
    {
      value: 'BROWSER_ACTION_ACTIVITY',
      action: () => {
        handleActivity()
      }
    }
  )

  return (
    <>
      <ActionsMenu {...settings} />
      {showRemoveModal && (
        <ModalRemove
          folderType={item}
          onClose={() => setShowRemoveModal(false)}
          onSuccess={() => {
            setShowRemoveModal(false)
            if (onDeleteSuccess) {
              onDeleteSuccess()
            }
          }}
        />
      )}
    </>
  )
}

export default TableMenu
